import {Location} from 'history';
import {To} from 'react-router';
import {Dispatch} from 'redux';
import {doNothing} from '~/library/toolbox';
import {createBrowserHistory, createDestination, createNextLocation, NavigationType} from './BrowserHistory';
import {
    getInitialHistoryStack,
    historyGo,
    historyPush,
    historyReplace,
    HistoryStack,
    HistoryStackAction,
    historyUpdate,
    reduceHistoryStack,
} from './HistoryStack';

export interface ControlledBrowserHistory {
    readonly location: Location;
    readonly action: NavigationType;

    /**
     * Writable callback that should execute whenever user
     * intends to navigate back in HARD mode and is inert in SOFT mode.
     */
    onBackPressed: () => void;

    // Navigator interface
    createHref: (to: To) => string;
    go: (delta: number) => void;
    push(to: To, state?: unknown): void;
    replace(to: To, state?: unknown): void;

    sync(state: HistoryStack): boolean;
    init(dependencies: {dispatch: Dispatch}): () => void;
    reducer(state: HistoryStack | undefined, action: HistoryStackAction): HistoryStack;
}

export function createHardControlledHistory({
    storage,
}: {
    storage: {
        get: () => HistoryStack | null;
        set: (value: HistoryStack) => void;
    };
}) {
    const browser = createBrowserHistory();

    const dispatch = createDispatchInjection();

    function getInitialState() {
        if (lastState?.location.key === browser.location.key) {
            return lastState;
        }
        return getInitialHistoryStack(browser.location, storage.get());
    }

    // eslint-disable-next-line no-var
    var lastState: HistoryStack = getInitialState();
    let persisted = false;
    let scheduledSyncId: ReturnType<typeof setTimeout> | undefined;

    let onBackPressed: () => void = () => {
        self.go(-1);
    };

    function persist() {
        if (!persisted) {
            storage.set(lastState);
            persisted = true;
        }
    }

    browser.listen(({action: navigationType}) => {
        if (navigationType !== NavigationType.Pop) {
            return;
        }

        onBackPressed();

        clearTimeout(scheduledSyncId);
        scheduledSyncId = setTimeout(() => {
            scheduledSyncId = undefined;
            self.sync(lastState);
        });
    });

    const self: ControlledBrowserHistory = {
        createHref: browser.createHref,

        set onBackPressed(callback) {
            onBackPressed = callback;
        },
        get onBackPressed() {
            return onBackPressed;
        },
        get location() {
            return browser.location;
        },
        get action() {
            return browser.action;
        },

        init(deps) {
            dispatch.inject(deps.dispatch);
            const ac = new AbortController();
            const signal = ac.signal;
            document.addEventListener('visibilitychange', () => document.hidden && persist(), {signal});
            window.addEventListener('pagehide', persist, {signal});

            return () => {
                dispatch.remove();
                ac.abort();
                persist();
            };
        },

        sync(state) {
            const {location, navigationType} = state;
            if (location.key === lastState.location.key) {
                return false;
            }
            clearTimeout(scheduledSyncId);
            lastState = state;
            persisted = false;
            let idx = window.history.state.idx;
            if (typeof idx !== 'number') {
                idx = 0;
            }
            const bufferSize = 2;
            const maxIndex = bufferSize - 1;
            if (idx >= maxIndex || navigationType === NavigationType.Replace) {
                browser.replace(location);
            } else {
                browser.push(location);
            }
            return true;
        },

        go(delta) {
            dispatch(historyGo(delta));
        },
        push(to, state) {
            dispatch(historyPush(createDestination(to, state)));
        },
        replace(to, state) {
            dispatch(historyReplace(createDestination(to, state)));
        },
        reducer(state = getInitialState(), action) {
            return reduceHistoryStack(state, action);
        },
    };
    return self;
}

export function createSoftControlledHistory() {
    const browser = createBrowserHistory();

    const dispatch = createDispatchInjection();

    browser.listen(({action: navigationType, location}) => {
        dispatch(historyUpdate({location, navigationType}));
    });

    const self: ControlledBrowserHistory = {
        createHref: browser.createHref,
        go: browser.go,
        onBackPressed: doNothing,

        get location() {
            return browser.location;
        },
        get action() {
            return browser.action;
        },

        init(deps) {
            dispatch.inject(deps.dispatch);
            return () => {
                dispatch.remove();
            };
        },
        sync(state) {
            const {location, navigationType} = state;
            if (location.key === browser.location.key) {
                return false;
            }
            if (navigationType === NavigationType.Push) {
                browser.push(location);
            }
            if (navigationType === NavigationType.Replace) {
                browser.replace(location);
            }
            return true;
        },
        push(to, state) {
            dispatch(historyPush(createDestination(to, state)));
        },
        replace(to, state) {
            dispatch(historyReplace(createDestination(to, state)));
        },
        reducer(state = getInitialHistoryStack(browser.location), action) {
            return reduceUnsureHistoryStack(state, action);
        },
    };
    return self;
}

interface UnsureHistoryStack extends HistoryStack {
    /**
     * This property required to make sure only latest history/update/(push|replace)
     * is applied. There can be cases when the app calls push or replace multiple
     * times before useEffect with sync is called.
     */
    keyToConfirm?: string;
}

function reduceUnsureHistoryStack(state: UnsureHistoryStack, action: HistoryStackAction): UnsureHistoryStack {
    if (historyPush.match(action)) {
        const next = createNextLocation(state.location, action, {indexDelta: 1});
        return {
            ...state,
            location: next,
            navigationType: NavigationType.Push,
            keyToConfirm: next.key,
        };
    }
    if (historyReplace.match(action)) {
        const next = createNextLocation(state.location, action, {indexDelta: 0});
        return {
            ...state,
            location: next,
            navigationType: NavigationType.Replace,
            keyToConfirm: next.key,
        };
    }
    if (historyUpdate.match(action)) {
        const {location, navigationType} = action.stack;
        if (
            NavigationType.Pop === navigationType ||
            state.keyToConfirm === location.key ||
            state.keyToConfirm === undefined
        ) {
            // Typescript cannot restrict objects from having extra fields.
            // UnsureHistoryStack extends HistoryEntry, meaning current state
            // can be passed to historyUpdate(). If that happens we need to make sure
            // keyToConfirm is removed
            return without('keyToConfirm', action.stack);
        }
    }
    return state;
}

function without<K extends keyof any, T extends object>(key: K, object: T) {
    if (key in object) {
        const {[key]: _, ...rest} = object;
        return rest;
    } else {
        return object;
    }
}

function createDispatchInjection() {
    const notInjected = () => {
        throw new Error('Dispatch is not injected');
    };
    let injected: Dispatch = notInjected;

    function dispatch(...args: Parameters<Dispatch>) {
        injected(...args);
    }
    dispatch.inject = (value: Dispatch) => {
        injected = value;
    };
    dispatch.remove = () => {
        injected = notInjected;
    };

    return dispatch;
}
